import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AttributeGroupListItem, useAttributeGroups } from '@/entities/attributes';
import { NestedItems } from '@/features/nested-items';
import { TextWithDescription } from '@ibsys/kit';
import { AttributeFormValues } from './validation';

type Props = {
  disabled?: boolean;
};

export const AttributeGroupsField: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['attributes', 'attributeGroups', 'common']);
  const { setValue, watch } = useFormContext<AttributeFormValues>();

  const { data: groupsData } = useAttributeGroups({ limit: 1000 });
  const allGroups = groupsData?.attributeGroups ?? [];

  const currentGroups = watch('attributeGroups') || [];

  // Преобразуем упрощенные объекты { code: string }[] в полные AttributeGroupListItem[]
  const selectedGroups = useMemo(() => {
    return currentGroups
      .map((g) => allGroups.find((group) => group.code === g.code))
      .filter((g): g is AttributeGroupListItem => g !== undefined);
  }, [currentGroups, allGroups]);

  const handleChange = (groups: AttributeGroupListItem[]) => {
    // Преобразуем обратно в упрощенные объекты { code: string }[]
    const simplifiedGroups = groups.map((g) => ({ code: g.code }));
    // Состав групп — такое же изменение карточки, как правка её поля: без `shouldDirty` кнопка сохранения остаётся неактивной
    setValue('attributeGroups', simplifiedGroups, { shouldDirty: true });
  };

  return (
    <NestedItems<AttributeGroupListItem>
      labels={{
        title: t('attributes:attributeGroups'),
        addButton: t('attributeGroups:addGroup'),
        removeButton: t('attributeGroups:removeGroupFromAttribute'),
        emptyState: t('attributeGroups:noGroupsInAttribute'),
        dialogTitle: t('attributeGroups:addGroup'),
        searchPlaceholder: t('common:search'),
      }}
      value={selectedGroups}
      onChange={handleChange}
      allItems={allGroups}
      getItemId={(item) => item.code}
      renderTitle={(item) => (
        <TextWithDescription
          text={<span>{item.name}</span>}
          description={<span className="font-mono text-sm font-medium">{item.code}</span>}
        />
      )}
      renderDescription={(item) => item.description}
      disabled={disabled}
      searchFilter={(item, searchQuery) => {
        const q = searchQuery.toLowerCase();
        return item.name.toLowerCase().includes(q) || item.code.toLowerCase().includes(q);
      }}
    />
  );
};
